import axios from 'axios';
import store from '../store';
import { IChatMessage } from '../components/GroupChat.vue';
import { GroupChatConversationType } from '../models/GroupChat';

export interface ChatAttachment {
  blobName: string;
  originalName: string;
  contentType: string;
  sizeBytes: number;
}

export interface GroupChatParticipant {
  userId: string;
  userName: string;
}

export interface GroupChatConversation {
  groupChatConversationId: number;
  groupChatConversationType: GroupChatConversationType;
  title: string | null;
  createdDateUtc: string;
  participants: GroupChatParticipant[];
}

const apiClient = axios.create({
  baseURL: process.env.VUE_APP_API_ROOT,
  withCredentials: false,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

export default {
  /**
   * Fetches all chat conversations visible to the current user.
   */
  async getConversations(): Promise<{
    groupName: string;
    conversations: GroupChatConversation[];
  }> {
    const token = store.getters.token;
    const response = await apiClient.get('/chat/conversations', {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  /**
   * Fetches same-group users that can be selected for a private chat.
   */
  async getParticipantCandidates(): Promise<GroupChatParticipant[]> {
    const token = store.getters.token;
    const response = await apiClient.get('/chat/participants/candidates', {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  /**
   * Creates a fixed-participant private chat conversation.
   */
  async createConversation(payload: {
    participantUserIds?: string[];
    title?: string | null;
    isPropipeAi?: boolean;
  }): Promise<GroupChatConversation> {
    const token = store.getters.token;
    const response = await apiClient.post('/chat/conversations', payload, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  /**
   * Removes the current user from a private chat conversation.
   */
  async leaveConversation(groupChatConversationId: number): Promise<void> {
    const token = store.getters.token;
    await apiClient.delete(
      `/chat/conversations/${groupChatConversationId}/participants/me`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
  },

  /**
   * Fetches up to 20 chat messages for the current user's group.
   * Pass beforeId for keyset pagination to load older messages.
   */
  async getMessages(
    beforeId?: number,
    groupChatConversationId?: number | null
  ): Promise<{ groupName: string; messages: IChatMessage[] }> {
    const token = store.getters.token;
    const params: Record<string, number> = {};
    if (beforeId != null) {
      params.beforeId = beforeId;
    }
    const url = groupChatConversationId
      ? `/chat/conversations/${groupChatConversationId}/messages`
      : '/chat/messages';
    const response = await apiClient.get(url, {
      params,
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = response.data;
    data.messages = data.messages.map(parseMessageAttachment);
    return data;
  },

  /**
   * Posts a new chat message. Returns the saved message with
   * server-assigned id and resolved userName.
   */
  async postMessage(
    text: string,
    attachment?: string | null,
    groupChatConversationId?: number | null
  ): Promise<IChatMessage> {
    const token = store.getters.token;
    const body: Record<string, string | boolean | number> = { text };
    if (attachment) {
      body.attachment = attachment;
    }
    const url = groupChatConversationId
      ? `/chat/conversations/${groupChatConversationId}/messages`
      : '/chat/messages';
    const response = await apiClient.post(url, body, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return parseMessageAttachment(response.data);
  },

  /**
   * Soft-deletes a chat message by id. Only the original author
   * can delete their own messages.
   */
  async deleteMessage(id: number): Promise<void> {
    const token = store.getters.token;
    await apiClient.delete(`/chat/messages/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  },

  /**
   * Toggles an emoji reaction on a message. Returns { added: boolean }.
   */
  async toggleReaction(
    messageId: number,
    emoji: string
  ): Promise<{ added: boolean }> {
    const token = store.getters.token;
    const response = await apiClient.post(
      `/chat/messages/${messageId}/reactions`,
      { emoji },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  /**
   * Uploads a file attachment. Returns attachment metadata.
   */
  async uploadFile(file: File): Promise<ChatAttachment> {
    const token = store.getters.token;
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post('/chat/upload', formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  /**
   * Fetches a chat attachment as a blob URL that can be used as an img src.
   * The backend proxies the blob content with auth handled via Bearer header.
   */
  async getAttachmentUrl(blobName: string): Promise<string> {
    const token = store.getters.token;
    const response = await apiClient.get('/chat/attachments', {
      params: { blobName },
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
    });
    return URL.createObjectURL(response.data);
  },
};

/**
 * Parses the attachmentJson string from the server response into a typed
 * attachment object on the message.
 */
function parseMessageAttachment(msg: any): IChatMessage {
  if (msg.attachmentJson) {
    try {
      msg.attachment = JSON.parse(msg.attachmentJson);
    } catch {
      msg.attachment = null;
    }
  } else {
    msg.attachment = msg.attachment || null;
  }
  delete msg.attachmentJson;
  return msg;
}
